import { useState, useEffect, useRef, useMemo } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { NavFn } from '../pages/SchedulerPage'
import type { ExtractResult } from './extractTypes'
import type { PreviewAssignResult } from './AssignPreviewView'
import { StepIndicator } from './SiteSelectView'
import { useCommonCodes } from '../context/CommonCodesContext'
import { useHolidays } from '../hooks/useHolidays'
import { useScheduleSave, scheduleErrorMessage, type OriginType, type SavePayload, type SaveWork } from '../hooks/useScheduleSave'
import { Button, Checkbox, Counter, FilterChip, Modal, Switch, Tooltip } from '@indipia/ui-core'
import { InfoBanner } from '../components/InfoBanner'
import { ScheduleReplaceModal } from '../components/ScheduleReplaceModal'

/** 공종 선택 화면 입력 상태 — [이전]·배정 확인 왕복 시 입력을 유지하려고 부모(SchedulerPage)가 보관한다. */
export type ExtractInputSnapshot = {
  checked: string[]                       // 선택된 공종 코드
  durations: Record<string, number>       // 공종 코드 → 소요일
  baseline: Record<string, number>        // 초기 소요일(바꾼 소요일 판정 기준)
  startDate: { year: number; month: number; day: number } | null
  endDate:   { year: number; month: number; day: number } | null
  autoAssign: boolean
  saveDefaults: boolean
  activeTemplateId: number | null
  /** 사용자가 입력을 한 번이라도 바꿨는지(이탈 경고 기준) */
  touched: boolean
}

type Props = {
  navigate: NavFn
  /** 어느 생성 흐름의 공종 선택인지 — auto: 견적서 자동 생성, direct: 직접 생성 */
  flow?: 'auto' | 'direct'
  extractResult?: ExtractResult | null
  /** 자동 배정 켜짐 — 배정 확인 화면으로 이동할 때 미리보기 결과와 저장에 쓸 값을 부모에 넘긴다. */
  onPreview?: (r: PreviewAssignResult, payload: SavePayload) => void
  /** 입력 상태 보관/복원 — snapshot 이 있으면 그 값으로 시작한다 */
  snapshot?: ExtractInputSnapshot | null
  onSnapshot?: (s: ExtractInputSnapshot) => void
  initYear?: number
  initMonth?: number
  /** 1단계(현장 선택)에서 고른 현장 id — 없으면 현장 미지정으로 생성 */
  selectedSiteId?: number
  /** 초기화(재생성) 흐름 전용 — 이미 스케줄이 있는 현장을 알고 시작하는 경우.
   *  지정되면 확인 없이 강제 교체(force=true)한다. */
  resetSiteId?: number
  /** 새 스케줄 생성 완료 콜백. 부모(SchedulerPage)의 handleScheduleCreated를 호출해
   *  현장 목록(allSites)을 다시 불러오도록 한다. 미지정 시 navigate('gantt', scheduleId)로 폴백. */
  onCreated?: (scheduleId: number) => void
  /** 스튜디오 홈에서 넘어온 진입모드(entry=QUOTE/MANUAL) — entry=CUSTOM은 '생성 방법 선택'
   *  (create-upload)을 건너뛰고 여기로 바로 들어오므로, "이전" 버튼도 그 단계로 돌아가면 안 되고
   *  현장 선택으로 돌아가야 한다. */
  entry?: string | null
  /** 생성 경로 — 진입 시점에 확정되어 흐름 끝까지 전달된다(MANUAL 직접 생성 · QUOTE 견적서 자동 생성). 마지막 단계에서 추정하지 않는다. */
  originType?: OriginType | null
}

const DN = ['일','월','화','수','목','금','토']
type DateVal = { year: number; month: number; day: number } | null
type WorkTypeTemplate = { templateId: number; templateName: string; sortOrder: number; workTypeCodes: string[] }

function cmp(a: DateVal, b: DateVal) {
  if (!a || !b) return 0
  if (a.year !== b.year) return a.year - b.year
  if (a.month !== b.month) return a.month - b.month
  return a.day - b.day
}
function inRange(s: DateVal, e: DateVal, c: DateVal) {
  return s && e && c && cmp(s, c) < 0 && cmp(c, e) < 0
}
function fmtDate(d: DateVal) {
  if (!d) return '선택하세요'
  return `${d.year}. ${d.month}. ${d.day}`
}
function toISO(d: DateVal) {
  if (!d) return ''
  return `${d.year}-${String(d.month).padStart(2,'0')}-${String(d.day).padStart(2,'0')}`
}
function diffDays(a: DateVal, b: DateVal, holidays: Set<string>) {
  if (!a || !b) return null
  const start = new Date(a.year, a.month - 1, a.day)
  const end   = new Date(b.year, b.month - 1, b.day)
  if (end < start) return null
  // 일요일·공휴일은 비작업일로 보고 공사 기간 일수에서 제외
  let count = 0
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    if (d.getDay() !== 0 && !holidays.has(iso)) count++
  }
  return count
}
function generateDays(year: number, month: number): (number | null)[][] {
  const firstDay = new Date(year, month - 1, 1).getDay()
  const lastDay  = new Date(year, month, 0).getDate()
  const flat: (number | null)[] = Array(firstDay).fill(null)
  for (let d = 1; d <= lastDay; d++) flat.push(d)
  while (flat.length % 7 !== 0) flat.push(null)
  const weeks: (number | null)[][] = []
  for (let i = 0; i < flat.length; i += 7) weeks.push(flat.slice(i, i + 7))
  return weeks
}
function shiftMonth(year: number, month: number, delta: number) {
  let m = month + delta, y = year
  while (m > 12) { m -= 12; y++ }
  while (m < 1)  { m += 12; y-- }
  return { year: y, month: m }
}

function MiniCalendar({ calYear, calMonth, calDays, startDate, endDate, onSelect, onPrev, onNext, holidays }: {
  calYear: number; calMonth: number
  calDays: (number | null)[][]
  startDate: DateVal; endDate: DateVal
  onSelect: (d: DateVal) => void
  onPrev?: () => void
  onNext?: () => void
  holidays: Set<string>
}) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        {onPrev
          ? <Button variant="secondary" size="sm" className="shrink-0" style={{ width: 24, height: 24, padding: 0 }} onClick={onPrev} aria-label="이전 달">‹</Button>
          : <div style={{ width: 24, flexShrink: 0 }} />
        }
        <div style={{ flex: 1, textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{calYear}년 {calMonth}월</div>
        {onNext
          ? <Button variant="secondary" size="sm" className="shrink-0" style={{ width: 24, height: 24, padding: 0 }} onClick={onNext} aria-label="다음 달">›</Button>
          : <div style={{ width: 24, flexShrink: 0 }} />
        }
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {DN.map((d, i) => (
              <th key={d} style={{ textAlign: 'center', padding: '4px 0', fontSize: 10, fontWeight: 600, color: i === 0 ? 'var(--status-danger-600)' : i === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface-2)' }}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calDays.map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => {
                if (!day) return <td key={di} style={{ width: `${100 / 7}%` }} />
                const cur: DateVal = { year: calYear, month: calMonth, day }
                const isSel    = (startDate && cmp(cur, startDate) === 0) || (endDate && cmp(cur, endDate) === 0)
                const isInRange = inRange(startDate, endDate, cur)
                const col = di
                const isHoliday = holidays.has(toISO(cur))
                const bg = isSel ? 'var(--gt-primary)' : isInRange ? 'var(--gt-subtle)' : 'transparent'
                const fg = isSel ? 'var(--shell-surface)' : isInRange ? 'var(--gt-primary)' : (col === 0 || isHoliday) ? 'var(--status-danger-600)' : col === 6 ? 'var(--status-info-600)' : 'var(--shell-on-surface)'
                return (
                  <td key={di} style={{ textAlign: 'center', padding: '2px 0', width: `${100 / 7}%` }}>
                    {/* width:100%+maxWidth(고정px 대신) — 두 달 달력이 나란히 들어가는 좁은 패널에서도
                        원의 크기가 칸 폭에 맞춰 줄어들 뿐 절대 카드 밖으로 넘치지 않는다. */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => onSelect(cur)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(cur) } }}
                      style={{ width: '100%', maxWidth: 26, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: 'var(--type-caption-size)', fontWeight: isSel ? 700 : isInRange ? 600 : 400, background: bg, color: fg, borderRadius: isSel ? '50%' : isInRange ? 3 : 4, cursor: 'pointer' }}
                    >
                      {day}
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ExtractView({ navigate, flow = 'direct', extractResult, onPreview, snapshot, onSnapshot, initYear, initMonth, selectedSiteId, resetSiteId, onCreated, entry, originType }: Props) {
  const user = useAuthStore(s => s.user)
  const { codes, loading: codesLoading } = useCommonCodes()

  const headers = {
    ...(user?.compId   ? { 'X-Comp-Id': String(user.compId) }   : {}),
    ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
  }

  // 공종 목록 = 마스터 사용 중 전체(자동생성 비대상 포함), 마스터 정렬순.
  const workTypes = useMemo(
    () => [...(codes?.workTypes ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [codes?.workTypes],
  )

  // 보관된 입력(이전 버튼·배정 확인 왕복)은 마운트 시점 1회만 복원한다. 모든 키는 공종 코드.
  const snap = useRef(snapshot).current
  const [checked,          setChecked]          = useState<Set<string>>(() => new Set(snap?.checked ?? []))
  const [durations,        setDurations]        = useState<Map<string, number>>(() => new Map(Object.entries(snap?.durations ?? {})))
  // 소요일 초기값(설정·견적서 추출값) — 이번에 바꾼 소요일 판정 기준
  const baselineRef = useRef<Map<string, number>>(new Map(Object.entries(snap?.baseline ?? {})))
  // "템플릿으로 빠르게 선택" — 직접 입력(견적서 없이 만들기) 흐름에서만 노출.
  const [templates,        setTemplates]        = useState<WorkTypeTemplate[]>([])
  const [activeTemplateId, setActiveTemplateId]  = useState<number | null>(snap?.activeTemplateId ?? null)
  const [codesInitialized, setCodesInitialized] = useState(!!snap)
  const [touched,          setTouched]          = useState(snap?.touched ?? false)
  // 설정값 조회(비동기)가 끝나기 전에 사용자가 이미 소요일을 직접 수정했다면,
  // 조회 결과로 덮어쓰지 않기 위한 플래그
  const durationsEditedRef = useRef(false)
  const [showOverrunConfirm, setShowOverrunConfirm] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  // 시공·자재 파트너 자동매칭 — 기본 켬. 켬이면 배정 확인 화면을 거쳐 저장, 끔이면 바로 저장.
  const [autoAssign, setAutoAssign] = useState(snap?.autoAssign ?? true)
  // 이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장 — 기본 해제
  const [saveDefaults, setSaveDefaults] = useState(snap?.saveDefaults ?? false)
  const [startDate,        setStartDate]        = useState<DateVal>(snap?.startDate ?? null)
  const [endDate,          setEndDate]          = useState<DateVal>(snap?.endDate ?? null)
  const [picking,          setPicking]          = useState<'start' | 'end'>(snap?.startDate && !snap?.endDate ? 'end' : 'start')
  const [baseMo,           setBaseMo]           = useState(() => {
    if (snap?.startDate) return { year: snap.startDate.year, month: snap.startDate.month }
    if (initYear && initMonth) return { year: initYear, month: initMonth }
    const t = new Date()
    return { year: t.getFullYear(), month: t.getMonth() + 1 }
  })
  // 공휴일(BE `/schedule/holidays`) — 두 달치 미니 달력 + 월 이동을 감안해 전후 1년 조회
  const holidays = useHolidays(`${baseMo.year - 1}-01-01`, `${baseMo.year + 1}-12-31`)

  const targetSiteId = resetSiteId ?? selectedSiteId ?? null

  // 저장 공용 로직(수동 배정 즉시 저장 · 기존 스케줄 교체 확인 · 오류 안내 · 기본 소요일 저장)
  const { saving, replaceOpen, replaceScheduleId, save, confirmReplace, cancelReplace } = useScheduleSave(scheduleId => {
    if (onCreated) onCreated(scheduleId)
    else navigate('gantt', scheduleId)
  })
  const busy = saving || previewing

  // 현장이 없으면(새로고침 등) 현장 선택 단계로 되돌린다.
  useEffect(() => {
    if (targetSiteId != null) return
    toast.info('현장 정보가 없어 현장 선택 화면으로 돌아왔어요')
    navigate('create-site')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!workTypes.length || codesInitialized) return
    // 기본 선택은 견적서 추출·템플릿으로 매칭된 공종만(그 외 해제). 직접 입력·추출 없음이면 전부 해제.
    const extractCodes = new Set(extractResult && !extractResult.skipped ? extractResult.works.map(w => w.workTypeCode) : [])
    setChecked(new Set(workTypes.filter(w => extractCodes.has(w.workTypeCode)).map(w => w.workTypeCode)))

    // 설정(공종별 기본 소요일)에서 저장된 값을 기본값으로 사용. GET /schedule/settings 의 workTypeDurations 는
    // 사업장 설정값이 없으면 마스터 표준 소요일(TM_WORK_TYPE.WORK_TYPE_DURATION)로 채워 내려오므로(결정 8) 직접 생성도
    // 마스터 표준값이 기본이 된다. 1일은 조회 실패·목록에 없는 공종일 때만 쓰는 최후 폴백.
    // AI 추출 결과가 있는 공종은 그 추출값이 우선(실제 견적서 기반이므로).
    const applyDurations = (savedByCode: Map<string, number>) => {
      const durMap = new Map<string, number>(workTypes.map(w => [w.workTypeCode, savedByCode.get(w.workTypeCode) ?? 1]))
      if (extractResult && !extractResult.skipped) {
        extractResult.works.forEach(ew => {
          if (ew.duration != null && durMap.has(ew.workTypeCode)) durMap.set(ew.workTypeCode, ew.duration)
        })
      }
      baselineRef.current = new Map(durMap)
      // 조회하는 동안 사용자가 이미 소요일을 직접 바꿨다면 그 값을 덮어쓰지 않음
      if (!durationsEditedRef.current) setDurations(durMap)
    }
    apiClient.get<{ data: { workTypeDurations: { workTypeCode: string; duration: number }[] } }>(
      '/schedule/settings', { headers }
    )
      .then(res => {
        const saved = res.data?.data?.workTypeDurations ?? []
        applyDurations(new Map(saved.map(s => [s.workTypeCode, s.duration])))
      })
      .catch(() => applyDurations(new Map()))
      .finally(() => setCodesInitialized(true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workTypes, extractResult, codesInitialized])

  useEffect(() => {
    if (!extractResult?.skipped) return
    apiClient.get<{ data: { templates: WorkTypeTemplate[] } }>('/schedule/work-type-templates', { headers })
      .then(res => {
        const list = res.data?.data?.templates ?? []
        setTemplates([...list].sort((a, b) => a.sortOrder - b.sortOrder))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractResult?.skipped])

  const isTouched = touched || startDate != null || endDate != null
  // 입력 보관(이전·배정 확인 왕복, 이탈 경고 기준) — 초기화가 끝난 뒤부터만 부모에 올린다.
  useEffect(() => {
    if (!codesInitialized) return
    onSnapshot?.({
      checked: [...checked],
      durations: Object.fromEntries(durations),
      baseline: Object.fromEntries(baselineRef.current),
      startDate, endDate, autoAssign, saveDefaults, activeTemplateId,
      touched: isTouched,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesInitialized, checked, durations, startDate, endDate, autoAssign, saveDefaults, activeTemplateId, isTouched])

  const goPrev = () => setBaseMo(prev => shiftMonth(prev.year, prev.month, -1))
  const goNext = () => setBaseMo(prev => shiftMonth(prev.year, prev.month, +1))

  const curMonthDays  = generateDays(baseMo.year, baseMo.month)
  const nextMo        = shiftMonth(baseMo.year, baseMo.month, 1)
  const nextMonthDays = generateDays(nextMo.year, nextMo.month)

  const toggle = (code: string) => {
    setActiveTemplateId(null)
    setTouched(true)
    setChecked(prev => {
      const next = new Set(prev)
      next.has(code) ? next.delete(code) : next.add(code)
      return next
    })
  }
  const toggleAll = () => {
    setActiveTemplateId(null)
    setTouched(true)
    if (checked.size === workTypes.length) {
      setChecked(new Set())
    } else {
      setChecked(new Set(workTypes.map(w => w.workTypeCode)))
    }
  }
  // 템플릿 칩 클릭 — 템플릿의 workTypeCodes에 해당하는 공종만 체크 상태로 바꾼다.
  const applyTemplate = (t: WorkTypeTemplate) => {
    setActiveTemplateId(t.templateId)
    setTouched(true)
    const codeSet = new Set(t.workTypeCodes)
    setChecked(new Set(workTypes.filter(w => codeSet.has(w.workTypeCode)).map(w => w.workTypeCode)))
  }
  const setDur = (code: string, val: number) => {
    durationsEditedRef.current = true
    setTouched(true)
    setDurations(prev => { const next = new Map(prev); next.set(code, val); return next })
  }
  const handleDateSelect = (d: DateVal) => {
    if (picking === 'start') {
      setStartDate(d); setEndDate(null); setPicking('end')
    } else {
      // 종료일 클릭이 시작일과 같은 날이거나 앞서면 종료일로 받지 않고 그 날을 새 시작일로 처리(기획 R-43)
      if (startDate && d && cmp(d, startDate) <= 0) {
        setStartDate(d); setEndDate(null); setPicking('end')
      } else {
        setEndDate(d); setPicking('start')
      }
    }
  }

  // 이전 — 선택한 현장·입력은 부모에 보관돼 있어 그대로 유지된다.
  // 자동 생성은 추출 결과 확인(create-extract-raw2), 직접 생성 entry=MANUAL 은 '생성 방법 선택'(create-upload)을
  // 건너뛰고 들어오므로 현장 선택으로, 초기화(재생성) 흐름은 원래대로 create-upload 로 돌아간다.
  const handleBack = () => {
    if (flow === 'auto') navigate('create-extract-raw2')
    else if (!resetSiteId && entry === 'MANUAL') navigate('create-site')
    else navigate('create-upload')
  }

  const duration = diffDays(startDate, endDate, holidays)
  const canNext  = targetSiteId != null && checked.size > 0 && !!startDate && !!endDate && !!duration

  const totalWorkDays = workTypes.reduce((sum, w) => checked.has(w.workTypeCode) ? sum + (durations.get(w.workTypeCode) ?? 1) : sum, 0)

  const selectedWorks = (): SaveWork[] => workTypes
    .filter(w => checked.has(w.workTypeCode))
    .map(w => ({ workTypeCode: w.workTypeCode, duration: durations.get(w.workTypeCode) ?? 1 }))

  // 이번에 바꾼 소요일(선택한 공종 중 초기값과 달라진 것) — 기본 소요일 저장 선택 노출·저장 대상
  const changedDurations = workTypes
    .filter(w => checked.has(w.workTypeCode) && durations.has(w.workTypeCode) && durations.get(w.workTypeCode) !== (baselineRef.current.get(w.workTypeCode) ?? 1))
    .map(w => ({ workTypeCode: w.workTypeCode, duration: durations.get(w.workTypeCode) as number }))

  // 자동매칭 카드 숫자 — 토글 켬 + 공종 1개 이상 + 소요일 초기화 완료일 때 요청 본문(JSON 문자열)을 만든다(기간 선택과 무관).
  // 기간을 보내지 않으면 서버가 일정 겹침 없이 '배정 가능한 후보가 있는 공종 수'만 계산한다(저장 없는 preview-assign).
  // 일정 겹침까지 반영한 실제 배정 결과는 생성 직후 '자동배정 확인' 화면에서 확정해 보여 준다.
  const previewKey = (autoAssign && codesInitialized && targetSiteId != null && checked.size > 0)
    ? JSON.stringify({
        siteId: targetSiteId,
        works:  selectedWorks(),
      })
    : null
  const [previewCounts,  setPreviewCounts]  = useState<{ foreman: number; vendor: number } | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    if (previewKey == null) { setPreviewCounts(null); setPreviewLoading(false); return }
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setPreviewLoading(true)
      apiClient.post<{ data: { assignments?: { partnerId: number | null; vendorId: number | null }[] } }>(
        '/schedule/schedules/preview-assign', JSON.parse(previewKey), { headers, signal: ctrl.signal }
      )
        .then(res => {
          if (ctrl.signal.aborted) return
          const rows = res.data?.data?.assignments ?? []
          setPreviewCounts({
            foreman: rows.filter(r => r.partnerId != null).length,
            vendor:  rows.filter(r => r.vendorId != null).length,
          })
        })
        .catch(() => { if (!ctrl.signal.aborted) setPreviewCounts(null) })
        .finally(() => { if (!ctrl.signal.aborted) setPreviewLoading(false) })
    }, 500)
    return () => { clearTimeout(timer); ctrl.abort() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey])

  // 생성 경로는 진입 시점에 확정된 값을 그대로 쓴다
  const buildPayload = (): SavePayload => ({
    flow,
    siteId:     targetSiteId as number,
    startDate:  toISO(startDate),
    endDate:    toISO(endDate),
    works:      selectedWorks(),
    autoAssign,
    originType: originType as OriginType,
    // resetSiteId(초기화 흐름)는 이미 "기존 스케줄 교체"를 사용자가 확인한 뒤 들어온 것이므로 바로 force=true.
    // 그 외는 force=false로 시도하고, 서버가 SCHEDULE_ALREADY_EXISTS 를 돌려주면 교체 확인창을 띄운다.
    force:      !!resetSiteId,
    defaultDurations: saveDefaults ? changedDurations : [],
  })

  const doSubmit = async () => {
    if (targetSiteId == null) return
    if (!originType) { toast.error('스케줄 생성 경로를 확인할 수 없어요. 처음부터 다시 시도해 주세요'); return }
    const payload = buildPayload()
    if (!autoAssign) {
      // 자동 배정 끔 — 배정 확인 없이 바로 저장 후 간트
      await save(payload)
      return
    }
    // 자동 배정 켬 — 저장 전 배정 확인 화면으로(미리보기는 저장 없이 배정 결과만 계산)
    setPreviewing(true)
    try {
      const res = await apiClient.post<{ data: PreviewAssignResult }>('/schedule/schedules/preview-assign', {
        siteId: payload.siteId, startDate: payload.startDate, endDate: payload.endDate, works: payload.works,
      }, { headers })
      onPreview?.(res.data.data, payload)
    } catch (err) {
      toast.error(scheduleErrorMessage(err, '배정 확인을 불러오지 못했어요. 잠시 후 다시 시도해 주세요'))
    } finally {
      setPreviewing(false)
    }
  }

  const handleNext = () => {
    if (!canNext || busy) return
    if (duration != null && totalWorkDays > duration) {
      setShowOverrunConfirm(true)
      return
    }
    void doSubmit()
  }

  const calHint = startDate && endDate
    ? '날짜를 클릭하면 새로 선택됩니다'
    : picking === 'end'
      ? '종료일을 클릭해 선택하세요'
      : '시작일을 클릭해 선택하세요'

  return (
    <div className="flex min-h-full flex-col justify-center bg-surface px-5 py-6">
      {/* 공종 소요일 입력의 브라우저 기본 위/아래 스피너 화살표 숨김 — 직접 타이핑만 가능하도록 */}
      <style>{`
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input.no-spinner { -moz-appearance: textfield; }
      `}</style>
      <div style={{ width: '100%', maxWidth: 1100, margin: '0 auto' }}>
      {/* 공종·공사일은 3단계(step=2) — 직접 생성(MANUAL)도 3단계 표시(F1 이동 규칙 §5). 초기화 흐름만 2단계 표시 */}
      <StepIndicator step={resetSiteId ? 1 : 2} resetMode={!!resetSiteId} />

      <InfoBanner>
        {extractResult?.skipped
          ? '원하는 공종과 기간을 직접 선택해서 나만의 공사 스케줄을 만들어보세요'
          : <>체크로 공종을 <strong>+/- 버튼</strong>으로 기간을 바로 확인하고 수정해보세요</>}
      </InfoBanner>

      {extractResult?.skipped && templates.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--shell-hint)', marginRight: 2 }}>템플릿으로 빠르게 선택</span>
          {templates.map(t => {
            const active = activeTemplateId === t.templateId
            return (
              <FilterChip
                key={t.templateId}
                active={active}
                aria-pressed={active}
                onClick={() => applyTemplate(t)}
              >
                {t.templateName}
              </FilterChip>
            )
          })}
        </div>
      )}

      {/* alignItems: flex-start — 왼쪽 공종 목록이 내부 스크롤 없이 내용만큼 늘어나므로(공종이 많으면
          꽤 길어진다), stretch로 오른쪽 "공사 기간 선택" 칼럼까지 그 높이에 맞춰 늘리면 아래
          space-between이 카드 사이 간격을 과도하게 벌려 달력과 요약/버튼이 뚝 떨어져 보인다.
          오른쪽은 자기 내용 높이만큼만 차지하게 둔다. */}
      <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start' }}>

        {/* ── 왼쪽: 공종 선택 ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderRadius: 16, border: '1px solid var(--shell-border)', background: 'var(--shell-surface)' }}>
          {/* 헤더 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--shell-border)', flexShrink: 0 }}>
            <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.3px' }}>공종 선택</span>
            <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 700, color: 'var(--gt-primary)', background: 'var(--gt-subtle)', padding: '3px 10px', borderRadius: 24, flexShrink: 0 }}>
              {checked.size} / {workTypes.length} 선택됨
            </span>
            <Button
              variant="secondary"
              size="sm"
              className="!h-auto shrink-0 !px-2.5 !py-1 text-[length:var(--type-caption-size)]"
              onClick={toggleAll}
            >
              {checked.size === workTypes.length ? '전체 해제' : '전체 선택'}
            </Button>
          </div>

          {/* 공종 그리드 */}
          <div style={{ padding: 10 }}>
            {codesLoading ? (
              <div aria-busy="true" style={{ padding: '32px 0', textAlign: 'center', fontSize: 12, color: 'var(--shell-on-surface-2)' }}>불러오는 중...</div>
            ) : workTypes.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)' }}>등록된 공종이 없습니다</div>
            ) : (
              /* gridTemplateColumns는 minmax(0,1fr)로 — 1fr만 쓰면 트랙이 내용물(소요일 스테퍼 등)의
                 min-content 폭 밑으로 줄어들지 않아, 좁은 화면에서 카드 밖으로 넘쳐 옆 카드와 겹친다. */
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 6 }}>
                {workTypes.map(w => {
                  const on  = checked.has(w.workTypeCode)
                  const dur = durations.get(w.workTypeCode) ?? 1
                  const dot = w.workTypeColor ?? 'var(--gt-primary)'
                  return (
                    <div
                      key={w.workTypeCode}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggle(w.workTypeCode)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(w.workTypeCode) } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
                        padding: '10px 12px', borderRadius: 12, cursor: 'pointer',
                        background: on ? 'var(--gt-subtle)' : 'var(--shell-bg)',
                        border: `var(--border-width-hairline) solid ${on ? dot : 'var(--shell-border)'}`,
                        transition: 'border-color .12s, background .12s',
                      }}
                    >
                      {/* 선택행 체크박스 — 행 전체가 클릭 토글이라 bare + 포인터·포커스 제외(이중 토글 방지) */}
                      <Checkbox
                        bare
                        color={dot}
                        checked={on}
                        onChange={() => {}}
                        tabIndex={-1}
                        aria-hidden="true"
                        className="pointer-events-none"
                      />
                      <span style={{ flex: 1, fontSize: 'var(--type-caption-size)', fontWeight: on ? 600 : 400, color: on ? 'var(--shell-on-surface)' : 'var(--shell-hint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-.2px' }}>
                        {w.workTypeName}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <Counter value={dur} onChange={v => setDur(w.workTypeCode, v)} min={1} size="sm" />
                        <span style={{ fontSize: 10, color: 'var(--shell-hint)' }}>일</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* 선택된 공종의 총 소요일수 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--shell-border)', flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>총 공종 일수</span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--gt-primary)' }}>
              {totalWorkDays}일
            </span>
          </div>
        </div>

        {/* ── 오른쪽: 공사 기간 선택 ── */}
        <div style={{ flex: '0 0 min(420px, 42%)', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* 달력 카드 */}
          <div style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 16, padding: '18px 16px' }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 16, letterSpacing: '-.3px' }}>공사 기간 선택</div>

            {/* 시작일 / 종료일 카드 */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => { setStartDate(null); setEndDate(null); setPicking('start') }}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStartDate(null); setEndDate(null); setPicking('start') } }}
                style={{ flex: 1, background: picking === 'start' ? 'var(--gt-subtle)' : 'var(--shell-bg)', border: picking === 'start' ? '2px solid var(--gt-primary)' : 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', transition: 'border .15s' }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gt-primary)', marginBottom: 4, letterSpacing: '.5px' }}>시작일</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: startDate ? 'var(--shell-on-surface)' : 'var(--shell-hint)', letterSpacing: '-.3px' }}>
                  {startDate ? fmtDate(startDate) : '날짜 선택...'}
                </div>
              </div>
              <div style={{ color: 'var(--shell-hint)', fontSize: 20, flexShrink: 0, paddingBottom: 2 }}>→</div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => { if (startDate) { setEndDate(null); setPicking('end') } }}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (startDate) { setEndDate(null); setPicking('end') } } }}
                style={{ flex: 1, background: picking === 'end' ? 'var(--gt-subtle)' : 'var(--shell-bg)', border: picking === 'end' ? '2px solid var(--gt-primary)' : 'var(--border-width-hairline) solid var(--shell-border)', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', transition: 'border .15s' }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--shell-hint)', marginBottom: 4, letterSpacing: '.5px' }}>종료일</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: endDate ? 'var(--shell-on-surface)' : 'var(--shell-hint)', letterSpacing: '-.3px' }}>
                  {endDate ? fmtDate(endDate) : '날짜 선택...'}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 10.5, color: 'var(--shell-hint)', marginBottom: 12, textAlign: 'center' }}>{calHint}</div>

            {/* 두 달 달력 */}
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
                <MiniCalendar
                  key={`${baseMo.year}-${baseMo.month}`}
                  calYear={baseMo.year} calMonth={baseMo.month} calDays={curMonthDays}
                  startDate={startDate} endDate={endDate}
                  onSelect={handleDateSelect}
                  onPrev={goPrev}
                  holidays={holidays}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid var(--shell-border)', paddingLeft: 10 }}>
                <MiniCalendar
                  key={`${nextMo.year}-${nextMo.month}`}
                  calYear={nextMo.year} calMonth={nextMo.month} calDays={nextMonthDays}
                  startDate={startDate} endDate={endDate}
                  onSelect={handleDateSelect}
                  onNext={goNext}
                  holidays={holidays}
                />
              </div>
            </div>
          </div>

          {/* 기간 요약 카드 */}
          <div style={{ background: 'var(--gt-subtle)', border: '1px solid var(--shell-border)', borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', marginBottom: 4 }}>선택 공종 {checked.size}개 기준</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--shell-on-surface-2)' }}>
                {startDate && endDate ? `${fmtDate(startDate)} → ${fmtDate(endDate)}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--gt-primary)', letterSpacing: '-.5px', lineHeight: 1 }}>
                {duration ?? ''}
              </div>
              <div style={{ fontSize: 13, color: 'var(--shell-hint)', fontWeight: 600 }}>일</div>
            </div>
          </div>

          {/* 시공·자재 파트너 자동매칭 카드 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '24px 16px', borderRadius: 12, border: '1px solid var(--shell-border)', background: autoAssign ? 'var(--gt-subtle)' : 'var(--shell-surface)', transition: 'background .15s' }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--shell-surface)', color: autoAssign ? 'var(--gt-primary)' : 'var(--shell-hint)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M19 8l1.5 1.5L23 7"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-.3px' }}>
                <Tooltip tip="매칭 기준: 스튜디오 설정 › 공종별 주시공파트너 · 발주-입고설정(주거래처)">시공·자재 파트너 자동매칭</Tooltip>
              </div>
              <div style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', marginTop: 4, letterSpacing: '-.2px' }}>
                {!autoAssign
                  ? '생성 후 간트차트에서 공종별로 직접 지정해요'
                  : previewKey == null
                    ? '공종을 선택하면 시공·자재 파트너를 자동 배정해요'
                    : previewCounts
                      ? (previewCounts.foreman === 0 && previewCounts.vendor === 0
                          ? '배정 가능한 파트너가 없어요. 생성 후 간트차트에서 직접 지정해 주세요'
                          : <>시공파트너 <b style={{ fontWeight: 700, color: 'var(--gt-primary)' }}>{previewCounts.foreman}</b> · 자재파트너 <b style={{ fontWeight: 700, color: 'var(--gt-primary)' }}>{previewCounts.vendor}</b>개 공종에 자동 배정돼요</>)
                      : previewLoading
                        ? '계산 중…'
                        : '공종을 선택하면 시공·자재 파트너를 자동 배정해요'}
              </div>
            </div>
            <Tooltip tip={autoAssign ? '자동매칭 끄기' : '자동매칭 켜기'}>
              <Switch checked={autoAssign} onChange={e => setAutoAssign(e.target.checked)} aria-label="시공·자재 파트너 자동매칭" />
            </Tooltip>
          </div>

          {/* 이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장(바꾼 소요일이 있을 때만 · 기본 해제) */}
          {changedDurations.length > 0 && (
            <Checkbox
              checked={saveDefaults}
              onChange={e => setSaveDefaults(e.target.checked)}
              label="이번에 바꾼 소요일을 앞으로의 기본 소요일로 저장"
            />
          )}

          {/* 버튼 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="secondary"
              size="lg"
              className="flex-1"
              onClick={handleBack}
            >
              이전
            </Button>
            <Button
              variant="primary"
              size="lg"
              className="flex-[2] text-[14px]"
              onClick={handleNext}
              disabled={!canNext || busy}
              loading={busy}
            >
              {busy ? '생성 중...' : '스케줄 생성하기'}
            </Button>
          </div>
        </div>

      </div>
      </div>

      {/* ── 공사 기간 초과 확인 다이얼로그 ────────────────────────────── */}
      <Modal
        open={showOverrunConfirm}
        onClose={() => setShowOverrunConfirm(false)}
        footer={<>
          <Button variant="secondary" onClick={() => setShowOverrunConfirm(false)}>취소</Button>
          <Button
            variant="primary"
            onClick={() => { setShowOverrunConfirm(false); void doSubmit() }}
          >진행</Button>
        </>}
      >
        <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-warning) 10%, var(--shell-surface))', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 25%, var(--shell-surface))', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L14.71 3.86a2 2 0 0 0-3.42 0z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 4 }}>공사 기간 초과</div>
            <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--color-warning)', lineHeight: 1.6 }}>
              총 공종 일수가 공사 기간보다 깁니다.<br />그래도 진행하시겠습니까?
            </div>
          </div>
        </div>
      </Modal>

      {/* ── 이미 스케줄이 있는 현장 확인 다이얼로그 ────────────────────── */}
      <ScheduleReplaceModal open={replaceOpen} onCancel={cancelReplace} onConfirm={confirmReplace} existingScheduleId={replaceScheduleId} />
    </div>
  )
}
